import { pickApiRowValue } from "./qmsApiUtils";
import { resolveQmsDashboardDateRange } from "./qmsDashboardUtils";

export const QMS_REPORT_GROUP_BY = [
  { key: "StaffId", label: "Staff", labelField: "StaffName" },
  { key: "RoleId", label: "Staff role", labelField: "Roles" },
  { key: "DocumentIdRef", label: "Document title", labelField: "DocumentTitle" },
  { key: "DocumentTypeId", label: "Document type", labelField: "DocumentType" },
  { key: "DocumentCategoryId", label: "Document category", labelField: "DocumentCategory" },
];

export function normalizeComplianceRow(row) {
  if (!row || typeof row !== "object") return null;
  const quizCorrect = pickApiRowValue(row, "CorrectAnswereCount", "CorrectAnswerCount", "quizCorrectCount");
  const quizTotal = pickApiRowValue(row, "TotalQuizQuestions", "quizQuestionCount");
  const userPct = pickApiRowValue(row, "UserPercentage", "userPercentage");
  const passPct = pickApiRowValue(row, "PassingPercentage", "passingPercentage");
  return {
    documentIdRef: String(pickApiRowValue(row, "DocumentIdRef", "documentIdRef") || ""),
    documentTitle: String(pickApiRowValue(row, "DocumentTitle", "documentTitle") || ""),
    documentUrl: String(pickApiRowValue(row, "DocumentURL", "documentUrl") || ""),
    documentExtension: String(pickApiRowValue(row, "DocumentExtension", "documentExtension") || ""),
    versionNumber: String(pickApiRowValue(row, "VersionNumber", "versionNumber") || ""),
    documentType: String(pickApiRowValue(row, "DocumentType", "documentType") || ""),
    documentTypeAbbr: String(
      pickApiRowValue(row, "DocumentTypeAbbr", "DocumentTypeAbberiviation", "documentTypeAbbr") || "",
    ),
    documentTypeId: String(pickApiRowValue(row, "DocumentTypeId", "documentTypeId") || ""),
    documentCategory: String(pickApiRowValue(row, "DocumentCategory", "documentCategory") || ""),
    documentCategoryAbbr: String(
      pickApiRowValue(
        row,
        "DocumentCategoryAbbr",
        "DocumentCategoryAbberiviation",
        "documentCategoryAbbr",
      ) || "",
    ),
    documentCategoryId: String(pickApiRowValue(row, "DocumentCategoryId", "documentCategoryId") || ""),
    documentCategoryUniqueNumber: String(
      pickApiRowValue(row, "DocumentCategoryUniqueNumber", "documentCategoryUniqueNumber") || "",
    ),
    staffId: String(pickApiRowValue(row, "StaffId", "staffId") || ""),
    staffName: String(pickApiRowValue(row, "StaffName", "staffName") || ""),
    employeeNum: String(pickApiRowValue(row, "EmployeeNum", "employeeNum") || ""),
    roleId: String(pickApiRowValue(row, "RoleId", "roleId") || ""),
    roles: String(pickApiRowValue(row, "Roles", "roles") || ""),
    isQuizApplicable: Number(pickApiRowValue(row, "IsQuizApplicable", "isQuizApplicable") || 0) === 1,
    isAcknowledged: Number(pickApiRowValue(row, "IsAcknowledged", "isAcknowledged") || 0) === 1,
    isPassed: Number(pickApiRowValue(row, "IsPassed", "isPassed") || 0) === 1,
    quizCorrectCount: quizCorrect == null || quizCorrect === "" ? null : Number(quizCorrect),
    quizQuestionCount: quizTotal == null || quizTotal === "" ? null : Number(quizTotal),
    userPercentage: userPct == null || userPct === "" ? null : Number(userPct),
    passingPercentage: passPct == null || passPct === "" ? null : Number(passPct),
    publishedDateTime: String(pickApiRowValue(row, "PublishedDateTime", "publishedDateTime") || ""),
    acknowledgementDateTime: String(
      pickApiRowValue(row, "AcknowledgementDateTime", "acknowledgementDateTime") || "",
    ),
  };
}

export function normalizeComplianceRows(rows) {
  return (rows || []).map(normalizeComplianceRow).filter(Boolean);
}

function parseRowDate(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function resolveReportDateRange(preset, startDate, endDate) {
  return resolveQmsDashboardDateRange({ preset, startDate, endDate });
}

export function filterComplianceRows(rows, filters = {}) {
  const {
    documentTypeIds = [],
    documentCategoryIds = [],
    staffIds = [],
    roleIds = [],
    datePreset = "All Time",
    startDate = "",
    endDate = "",
  } = filters;

  let result = [...(rows || [])];

  if (documentTypeIds.length > 0) {
    result = result.filter((row) => documentTypeIds.includes(row.documentTypeId));
  }
  if (documentCategoryIds.length > 0) {
    result = result.filter((row) => documentCategoryIds.includes(row.documentCategoryId));
  }
  if (staffIds.length > 0) {
    result = result.filter((row) => staffIds.includes(row.staffId));
  }
  if (roleIds.length > 0) {
    result = result.filter((row) => roleIds.includes(row.roleId));
  }

  if (datePreset && datePreset !== "All Time") {
    const range = resolveReportDateRange(datePreset, startDate, endDate);
    if (range.startDate && range.endDate) {
      const start = new Date(`${range.startDate}T00:00:00`);
      const end = new Date(`${range.endDate}T23:59:59`);
      result = result.filter((row) => {
        const published = parseRowDate(row.publishedDateTime);
        if (!published) return true;
        return published >= start && published <= end;
      });
    }
  }

  return result;
}

export function isComplianceRowRead(row) {
  return Boolean(row?.isAcknowledged);
}

export function calculateReadPercentage(readCount, unreadCount) {
  const total = readCount + unreadCount;
  if (!total) return 0;
  return Math.round((readCount / total) * 100);
}

export function groupComplianceSummary(rows, groupByKey = "StaffId") {
  const groupMeta = QMS_REPORT_GROUP_BY.find((item) => item.key === groupByKey) || QMS_REPORT_GROUP_BY[0];
  const groups = new Map();

  for (const row of rows || []) {
    const groupId = String(row[groupMeta.key.replace("IdRef", "IdRef").replace("StaffId", "staffId").replace("RoleId", "roleId").replace("DocumentIdRef", "documentIdRef").replace("DocumentTypeId", "documentTypeId").replace("DocumentCategoryId", "documentCategoryId")] ?? "");
    // Map API keys to normalized row fields
    const id =
      groupByKey === "StaffId"
        ? row.staffId
        : groupByKey === "RoleId"
          ? row.roleId
          : groupByKey === "DocumentIdRef"
            ? row.documentIdRef
            : groupByKey === "DocumentTypeId"
              ? row.documentTypeId
              : row.documentCategoryId;

    const label =
      groupByKey === "StaffId"
        ? row.staffName || "Unknown staff"
        : groupByKey === "RoleId"
          ? row.roles || "Unknown role"
          : groupByKey === "DocumentIdRef"
            ? `${row.documentTitle || "Untitled"}${row.documentExtension ? `.${row.documentExtension}` : ""}`
            : groupByKey === "DocumentTypeId"
              ? row.documentType || "Unknown type"
              : row.documentCategory || "Unknown category";

    if (!groups.has(id)) {
      groups.set(id, { id, label, read: 0, unread: 0, total: 0, pct: 0, rows: [] });
    }
    const bucket = groups.get(id);
    bucket.rows.push(row);
    if (isComplianceRowRead(row)) bucket.read += 1;
    else bucket.unread += 1;
    bucket.total = bucket.read + bucket.unread;
    bucket.pct = calculateReadPercentage(bucket.read, bucket.unread);
  }

  return {
    groupLabel: groupMeta.label,
    rows: Array.from(groups.values()).sort((a, b) => b.unread - a.unread || b.total - a.total),
  };
}

export function buildComplianceDetailColumns() {
  return [
    { key: "documentTitle", label: "Document title", sortable: true },
    { key: "versionNumber", label: "Version", sortable: true },
    { key: "documentType", label: "Document type", sortable: true },
    { key: "documentCategory", label: "Document category", sortable: true },
    { key: "staffName", label: "Staff", sortable: true },
    { key: "roles", label: "Role", sortable: true },
    { key: "quizLabel", label: "Quiz", sortable: true },
    { key: "readLabel", label: "Status", sortable: true },
    { key: "publishedDateTime", label: "Assigned", sortable: true },
    { key: "acknowledgementDateTime", label: "Acknowledged", sortable: true },
  ];
}

export function mapComplianceDetailRows(rows) {
  return (rows || []).map((row, index) => ({
    id: `${row.documentIdRef}-${row.staffId}-${index}`,
    documentTitle: `${row.documentTitle || "Untitled"}${row.documentExtension ? `.${row.documentExtension}` : ""}`,
    versionNumber: row.versionNumber || "—",
    documentType: row.documentType || "—",
    documentCategory: row.documentCategory || "—",
    staffName: row.staffName || "—",
    roles: row.roles || "—",
    quizLabel: row.isQuizApplicable ? "Yes" : "No",
    readLabel: isComplianceRowRead(row) ? "Read" : "Unread",
    publishedDateTime: row.publishedDateTime || "—",
    acknowledgementDateTime: row.acknowledgementDateTime || "—",
  }));
}

export function mapComplianceSummaryTableRows(summaryRows) {
  return (summaryRows || []).map((row) => ({
    id: row.id,
    label: row.label,
    read: row.read,
    unread: row.unread,
    pct: `${row.pct}%`,
    pctValue: row.pct,
  }));
}

export function buildSummaryTableColumns(groupLabel) {
  return [
    { key: "label", label: groupLabel, sortable: true },
    { key: "read", label: "Read", sortable: true },
    { key: "unread", label: "Unread", sortable: true },
    { key: "pct", label: "Read %", sortable: true },
  ];
}

export function buildChartDataFromSummary(summaryRows) {
  const labels = (summaryRows || []).slice(0, 12).map((row) => row.label);
  return {
    labels,
    datasets: [
      {
        label: "Read",
        data: (summaryRows || []).slice(0, 12).map((row) => row.read),
        backgroundColor: "#16a34a",
      },
      {
        label: "Unread",
        data: (summaryRows || []).slice(0, 12).map((row) => row.unread),
        backgroundColor: "#dc2626",
      },
    ],
  };
}

export function autoColumnsFromRows(rows, preferred = []) {
  if (!rows?.length) return [];
  const keys = preferred.length
    ? preferred.filter((key) => key in rows[0])
    : Object.keys(rows[0]).filter((key) => key !== "id");
  return keys.map((key) => ({
    key,
    label: key
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (c) => c.toUpperCase())
      .trim(),
    sortable: true,
  }));
}

export function mapGenericReportRows(rows, idKeys = []) {
  return (rows || []).map((row, index) => {
    const id =
      idKeys.map((key) => row[key]).find(Boolean) ||
      row.Id ||
      row.id ||
      row.DocumentId ||
      row.documentId ||
      index;
    return { id: String(id), ...row };
  });
}
